/**
 * Fire GA4 `select_item` when a shopper clicks TN "Buy Tickets" and the
 * quantity / pre-checkout drawer successfully opens.
 */

import { trackSecondarySelectItem } from './ga4SecondaryMarketplace';

export const PRECHECKOUT_CTA_ID = 'pre-checkout-price-cta';

const BUY_CTA_SELECTOR =
  '.venue-ticket-list-cta-button, .promotix-seatics-buy-cta, button.venue-ticket-list-cta-js';

const TICKET_ROW_SELECTOR =
  'tr.venue-ticket-list-row-js, tr.Sea-TicketRow, .venue-ticket-list-row-js';

const parseMoney = value => {
  const amount = Number(String(value || '').replace(/,/g, ''));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

export const parseDisplayedUnitPriceFromRow = row => {
  if (!(row instanceof HTMLElement)) return null;
  const dataPrice = parseMoney(row.getAttribute('data-price'));
  if (dataPrice != null) return dataPrice;

  const amtEl = row.querySelector('.venue-ticket-list-cta-amt, [class*="cta-amt"]');
  if (amtEl) {
    const match = (amtEl.textContent || '').match(/\$\s*(\d{1,4}(?:,\d{3})*(?:\.\d{2})?)/);
    if (match) return parseMoney(match[1]);
  }

  const text = row.textContent || '';
  const match = text.match(/\$\s*(\d{1,4}(?:,\d{3})*(?:\.\d{2})?)\s*\/\s*ea\b/i);
  if (match) return parseMoney(match[1]);
  return null;
};

export const parseSectionRowFromTicketRow = row => {
  const text = String(row?.textContent || '');
  const secMatch = text.match(/\b(?:Sec(?:tion)?\.?)\s*([A-Za-z0-9/-]+)/i);
  const rowMatch = text.match(/\bRow\s*([A-Za-z0-9/-]+)/i);
  return {
    section: secMatch ? String(secMatch[1]).trim() : null,
    row: rowMatch ? String(rowMatch[1]).trim() : null,
  };
};

export const ticketGroupIdFromDom = row => {
  if (!(row instanceof HTMLElement)) return null;
  const attrNames = [
    'data-tg-id',
    'data-tgid',
    'data-ticket-group-id',
    'data-ticketgroupid',
    'data-id',
  ];
  for (const name of attrNames) {
    const direct = row.getAttribute(name);
    if (direct && String(direct).trim()) return String(direct).trim();
    const nested = row.querySelector(`[${name}]`);
    const nestedVal = nested?.getAttribute?.(name);
    if (nestedVal && String(nestedVal).trim()) return String(nestedVal).trim();
  }
  return null;
};

export const resolveTicketGroupFromRow = (row, ticketGroups = []) => {
  const groups = Array.isArray(ticketGroups) ? ticketGroups : [];
  const domId = ticketGroupIdFromDom(row);
  if (domId) {
    const byId = groups.find(
      g => String(g?.tgID || g?.tgId || '').trim() === domId
    );
    if (byId) return byId;
  }

  const { section, row: seatRow } = parseSectionRowFromTicketRow(row);
  const price = parseDisplayedUnitPriceFromRow(row);
  let candidates = groups.filter(g => {
    const sec = String(g?.tgUserSec || g?.section || '').trim();
    const rw = String(g?.tgUserRow || g?.row || '').trim();
    // When the row exposes a section/row, require an actual match (empty group
    // fields do not count as a wild match).
    const secOk =
      !section ||
      (Boolean(sec) && sec.toLowerCase() === String(section).toLowerCase());
    const rowOk =
      !seatRow || (Boolean(rw) && rw.toLowerCase() === String(seatRow).toLowerCase());
    return secOk && rowOk;
  });

  if (price != null) {
    const byPrice = (candidates.length ? candidates : groups).filter(g => {
      const p = Number(g?.tgPrice ?? g?.tgBasePrice);
      return Number.isFinite(p) && Math.abs(p - price) < 0.01;
    });
    if (byPrice.length) candidates = byPrice;
  }

  if (candidates.length === 1) return candidates[0];
  if (domId) {
    return { tgID: domId, tgUserSec: section, tgUserRow: seatRow, tgPrice: price };
  }
  if (candidates.length > 1) return candidates[0];
  return null;
};

export const deliveryMethodFromTicketGroup = ticketGroup => {
  const raw =
    ticketGroup?.tgDeliveryMethod ||
    ticketGroup?.deliveryMethod ||
    ticketGroup?.DeliveryMethod ||
    (Array.isArray(ticketGroup?.deliveryMethods)
      ? ticketGroup.deliveryMethods[0]
      : null);
  const s = String(raw || '').trim();
  return s || null;
};

export const isPrecheckoutDrawerOpen = (root = typeof document !== 'undefined' ? document : null) => {
  if (!root) return false;
  const cta =
    (typeof root.getElementById === 'function' && root.getElementById(PRECHECKOUT_CTA_ID)) ||
    root.querySelector?.(`#${PRECHECKOUT_CTA_ID}`);
  if (!(cta instanceof HTMLElement)) return false;
  if (cta.hasAttribute('hidden')) return false;
  const style =
    typeof window !== 'undefined' && window.getComputedStyle
      ? window.getComputedStyle(cta)
      : null;
  if (style) {
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
  }
  // Presence of the pre-checkout CTA is enough — jsdom reports 0x0 geometry.
  return true;
};

export const waitForPrecheckoutDrawer = ({
  timeoutMs = 2500,
  root = typeof document !== 'undefined' ? document : null,
  isCancelled = () => false,
} = {}) =>
  new Promise(resolve => {
    if (!root) {
      resolve(false);
      return;
    }
    if (isPrecheckoutDrawerOpen(root)) {
      resolve(true);
      return;
    }
    const started = Date.now();
    let settled = false;
    const finish = ok => {
      if (settled) return;
      settled = true;
      try {
        observer.disconnect();
      } catch (e) {
        // ignore
      }
      clearInterval(poll);
      clearTimeout(timer);
      resolve(Boolean(ok));
    };
    const check = () => {
      if (isCancelled()) {
        finish(false);
        return;
      }
      if (isPrecheckoutDrawerOpen(root)) {
        finish(true);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        finish(false);
      }
    };
    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(check)
        : { disconnect() {}, observe() {} };
    const observeRoot =
      root.body || root.documentElement || (root.nodeType === 1 ? root : null);
    if (observeRoot && typeof observer.observe === 'function') {
      observer.observe(observeRoot, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
    const poll = setInterval(check, 100);
    const timer = setTimeout(() => finish(isPrecheckoutDrawerOpen(root)), timeoutMs);
  });

/**
 * @param {object} options
 * @param {object} options.listing
 * @param {() => object[]} options.getTicketGroups
 * @param {() => boolean} [options.isCancelled]
 * @param {string} [options.mapContainerId]
 * @returns {() => void} cleanup
 */
export const installSeaticsSelectItemTracking = ({
  listing,
  getTicketGroups,
  isCancelled = () => false,
  mapContainerId = 'tn-maps',
} = {}) => {
  if (typeof document === 'undefined' || !listing) return () => {};

  const onClick = event => {
    if (isCancelled()) return;
    const el = event.target;
    if (!(el instanceof Element)) return;

    const btn = el.closest(BUY_CTA_SELECTOR);
    if (!btn) return;

    const inMap = btn.closest(`#${mapContainerId}, #sea-mp, [data-seatics-chrome="1"]`);
    if (!inMap) return;

    // Ignore the pre-checkout "Buy Tickets — $X/ea" CTA (that path is secondary_ticket_selected).
    if (btn.id === PRECHECKOUT_CTA_ID || btn.closest(`#${PRECHECKOUT_CTA_ID}`)) return;

    const row = btn.closest(TICKET_ROW_SELECTOR);
    const groups =
      typeof getTicketGroups === 'function' ? getTicketGroups() || [] : [];
    const ticketGroup = resolveTicketGroupFromRow(row, groups);
    const seat = parseSectionRowFromTicketRow(row);
    const tgId = String(
      ticketGroup?.tgID || ticketGroup?.tgId || ticketGroupIdFromDom(row) || ''
    ).trim();
    if (!tgId) return;

    const unitPrice =
      Number(ticketGroup?.tgPrice) > 0
        ? Number(ticketGroup.tgPrice)
        : parseDisplayedUnitPriceFromRow(row);

    void waitForPrecheckoutDrawer({ isCancelled }).then(opened => {
      if (!opened || isCancelled()) return;
      trackSecondarySelectItem({
        listing,
        ticketGroupId: tgId,
        ticketSection: ticketGroup?.tgUserSec || ticketGroup?.section || seat.section,
        ticketRow: ticketGroup?.tgUserRow || ticketGroup?.row || seat.row,
        ticketUnitPrice: unitPrice,
        currency: listing?.attributes?.price?.currency || 'USD',
        deliveryMethod: deliveryMethodFromTicketGroup(ticketGroup),
      });
    });
  };

  document.addEventListener('click', onClick, true);
  return () => {
    document.removeEventListener('click', onClick, true);
  };
};
